import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Dashboard } from '../../api/admin';

type ServiceStatus = 'ok' | 'error' | 'idle';

function ServiceLine({
  name,
  status,
  meta,
}: {
  name: string;
  status: ServiceStatus;
  meta?: string;
}) {
  const color =
    status === 'ok'
      ? 'var(--green)'
      : status === 'error'
        ? 'var(--magenta)'
        : 'var(--coral)';
  const label = status === 'ok' ? 'UP' : status === 'error' ? 'DOWN' : 'IDLE';
  return (
    <div className="flex items-baseline gap-3 py-2">
      <span
        className="inline-block size-1.5 shrink-0"
        style={{ background: color }}
        aria-hidden
      />
      <span className="flex-1 font-semibold">{name}</span>
      {meta && <span className="mono text-xs text-muted">{meta}</span>}
      <span className="mono text-[10px] tracking-widest" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

function QueueRow({
  label,
  counts,
}: {
  label: string;
  counts: Record<string, number> | null;
}) {
  const failed = counts?.failed ?? 0;
  const total =
    counts !== null ? Object.values(counts).reduce((s, n) => s + n, 0) : 0;
  return (
    <div className="border-t border-t-rule py-2">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <span className="font-semibold">{label}</span>
        <span className="display text-xl">{total}</span>
      </div>
      {counts !== null ? (
        <div className="mono flex gap-4 text-xs text-muted">
          <span>
            wait <span className="text-ink">{counts.waiting ?? 0}</span>
          </span>
          <span>
            active <span className="text-blue">{counts.active ?? 0}</span>
          </span>
          <span>
            done <span className="text-ink">{counts.completed ?? 0}</span>
          </span>
          <span>
            failed{' '}
            <span
              style={{ color: failed > 0 ? 'var(--magenta)' : 'var(--muted)' }}
            >
              {failed}
            </span>
          </span>
        </div>
      ) : (
        <p className="label text-muted">unavailable</p>
      )}
    </div>
  );
}

interface DashboardTabProps {
  data?: Dashboard & { latencyMs: number };
  isLoading: boolean;
  isFetching?: boolean;
  dataUpdatedAt?: number;
  onRefresh: () => void;
}

/**
 * "Last updated Xs ago", ticking once a second. Its own component so the
 * 1 Hz clock re-renders one <span>, not the whole dashboard tree.
 */
function LastUpdated({ dataUpdatedAt }: { dataUpdatedAt?: number }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const ageSec = dataUpdatedAt ? Math.floor((now - dataUpdatedAt) / 1000) : 0;
  const label =
    ageSec < 2
      ? t('admin.dashboard.lastUpdatedNow', 'à l’instant')
      : ageSec < 60
        ? t('admin.dashboard.lastUpdatedSeconds', 'il y a {{count}}s', {
            count: ageSec,
          })
        : t('admin.dashboard.lastUpdatedMinutes', 'il y a {{count}}min', {
            count: Math.floor(ageSec / 60),
          });

  return <span className="mono ml-auto text-xs text-muted">{label}</span>;
}

export function DashboardTab({
  data,
  isLoading,
  isFetching,
  dataUpdatedAt,
  onRefresh,
}: DashboardTabProps) {
  const { t } = useTranslation();

  if (isLoading || !data) {
    return <p className="label cursor py-6">LOADING</p>;
  }

  const allGreen =
    data.services.db === 'ok' &&
    data.services.redis === 'ok' &&
    data.services.s3 === 'ok' &&
    data.services.worker === 'ok';

  const failedTotal =
    (data.queue.generate?.failed ?? 0) + (data.queue.finalize?.failed ?? 0);

  return (
    <div>
      {/* compact summary bar */}
      <div className="mb-6 flex items-center gap-4 border-b border-b-rule pb-4">
        <span
          className="inline-flex items-center gap-2 text-sm font-bold tracking-widest uppercase"
          style={{ color: allGreen ? 'var(--ink)' : 'var(--magenta)' }}
        >
          <span
            className="inline-block size-2"
            style={{ background: allGreen ? 'var(--green)' : 'var(--magenta)' }}
          />
          {allGreen
            ? t('admin.dashboard.healthy', 'OPERATIONAL')
            : t('admin.dashboard.degraded', 'DEGRADED')}
        </span>
        <span className="mono text-xs text-muted">· {data.latencyMs}ms</span>
        <LastUpdated dataUpdatedAt={dataUpdatedAt} />
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          className="btn-link leading-(--text-xs--line-height)"
        >
          {isFetching
            ? t('admin.dashboard.refreshing', 'rafraîchissement…')
            : t('admin.dashboard.refresh', 'rafraîchir')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Services */}
        <section className="epi-card lg:col-span-2">
          <div className="epi-card-head">
            <span className="num">S</span>
            <span className="title">
              {t('admin.dashboard.servicesTitle', 'Services')}
            </span>
            <span className="meta">runtime</span>
          </div>
          <div>
            <ServiceLine name="Postgres" status={data.services.db} />
            <div className="border-t border-t-rule">
              <ServiceLine name="Redis" status={data.services.redis} />
            </div>
            <div className="border-t border-t-rule">
              <ServiceLine name="S3" status={data.services.s3} />
            </div>
            <div className="border-t border-t-rule">
              <ServiceLine
                name="Worker"
                status={data.services.worker}
                meta={`${data.services.workerCount} ${t(
                  'admin.dashboard.workerCount',
                  'instances',
                )}`}
              />
            </div>
          </div>
        </section>

        {/* Counts */}
        <section className="epi-card">
          <div className="epi-card-head">
            <span className="num">N</span>
            <span className="title">
              {t('admin.dashboard.countsTitle', 'Données')}
            </span>
            <span className="meta">db</span>
          </div>
          <div className="space-y-3">
            <div>
              <p className="label">
                {t('admin.dashboard.userCount', 'utilisateurs')}
              </p>
              <p className="display mt-1 text-3xl">{data.stats.users ?? '—'}</p>
            </div>
            <div className="border-t border-t-rule pt-3">
              <p className="label">
                {t('admin.dashboard.templateCount', 'templates actifs')}
              </p>
              <p className="display mt-1 text-3xl">
                {data.stats.templates ?? '—'}
              </p>
            </div>
          </div>
        </section>

        {/* Queues — full width */}
        <section className="epi-card lg:col-span-3">
          <div className="epi-card-head">
            <span className="num">Q</span>
            <span className="title">
              {t('admin.dashboard.queuesTitle', 'Files BullMQ')}
            </span>
            <span className="meta">
              {failedTotal > 0 ? (
                <Link to="/admin/logs" className="btn-link text-[11px]">
                  {t('admin.dashboard.failedJobs', { count: failedTotal })} →
                </Link>
              ) : (
                'idle'
              )}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <QueueRow label="cards-generate" counts={data.queue.generate} />
            <QueueRow label="cards-finalize" counts={data.queue.finalize} />
          </div>
        </section>
      </div>
    </div>
  );
}
